import { useMemo } from 'react'
import { usePageParams } from '../../utils/usePageParams'
import { serializeFilterValues } from '../../utils/filterValues'
import { filtersToUrlParams, mergeFilter, urlParamsToFilters, type FilterBy } from './FilterTemplate'
import { addUrlParamPrefix, addUrlParamPrefixToKey, stripUrlParamPrefixFromKey } from './filterParams'

export interface UrlFilterOptions<T extends object> {
    mapUrlParamToFilterFn: (key: keyof T, value: string) => FilterBy | undefined
    mapFilterToUrlParamFn: (filter: FilterBy) => Partial<T>
    pageParamName?: string
    urlParamPrefix?: string
}

/** Keeps chips, column clicks and pagination in sync through the list's existing URL codecs. */
export function useUrlFilters<T extends object>({
    mapUrlParamToFilterFn,
    mapFilterToUrlParamFn,
    pageParamName = 'pageNumber',
    urlParamPrefix,
}: UrlFilterOptions<T>) {
    const [params, setParams] = usePageParams<T>()
    const decode = useMemo(
        () => (key: keyof T, value: string) => {
            if (!urlParamPrefix) return mapUrlParamToFilterFn(key, value)
            if (!String(key).startsWith(urlParamPrefix)) return undefined
            return mapUrlParamToFilterFn(stripUrlParamPrefixFromKey(urlParamPrefix, String(key)) as keyof T, value)
        },
        [mapUrlParamToFilterFn, urlParamPrefix]
    )
    const encode = useMemo(
        () =>
            (filter: FilterBy): Partial<T> => {
                const encoded = mapFilterToUrlParamFn(filter)
                return urlParamPrefix ? (addUrlParamPrefix(urlParamPrefix, encoded) as Partial<T>) : encoded
            },
        [mapFilterToUrlParamFn, urlParamPrefix]
    )
    const appliedFilters = useMemo(() => urlParamsToFilters(params, decode), [params, decode])
    const onFiltersApplied = (next: FilterBy[]) => {
        const pageKey = urlParamPrefix ? addUrlParamPrefixToKey(urlParamPrefix, pageParamName) : pageParamName
        setParams({ ...filtersToUrlParams(next, appliedFilters, encode), [pageKey]: '0' } as T)
    }
    const applyColumnFilter = (param: keyof T & string, value: string) => {
        let incoming = mapUrlParamToFilterFn(param, value)
        if (!incoming) return
        // A raw cell value may itself contain a comma; encode it as one multi-value entry.
        if (incoming.operator === 'isOneOf') {
            incoming = mapUrlParamToFilterFn(param, serializeFilterValues([value])) ?? incoming
        }
        const next = mergeFilter(appliedFilters, incoming)
        if (next !== appliedFilters) onFiltersApplied(next)
    }
    return { appliedFilters, onFiltersApplied, applyColumnFilter }
}
